/**
 * task-live-status — ONE truth ladder for "what is this task doing right now",
 * shared by the composer's receipt card and task dots (team feedback 08-04:
 * the receipt claimed "Running" the moment a task was created while the board
 * honestly said "Queued", then everything visibly shifted to running when the
 * agent actually started — two surfaces, two stories).
 *
 * Ladder — the first rung that KNOWS wins:
 *   1. live socket phase   processing/waiting/interrupting = running (the turn
 *                          is provably active); error = failed. 'idle' means
 *                          BOTH "not started yet" and "turn finished" (founder
 *                          07-17: a newborn dot flashed green) — it can never
 *                          decide anything, so it falls through to the rows.
 *   2. story spine         server-minted closed vocabulary (push-updated,
 *                          reload-proof) — outranks legacy lifecycle.
 *   3. legacy lifecycle    fallback for pre-spine rows.
 *   4. newborn             no row anywhere yet: queued. A just-created task
 *                          CANNOT already be running — AGENT_START hasn't
 *                          fired — so the receipt never overclaims.
 *
 * Vocabulary matches the board (WorkingNowSection says "Queued"): one state,
 * one word, every surface.
 */

import type { TaskPhase } from "@/stores/types";

export type TaskLiveStatus =
  | "queued"
  | "running"
  | "needs"
  | "done"
  | "stopped"
  // The ledger halted the turn — settled like a stop, but never the user's
  // hand (founder 09-03: it read "Stopped").
  | "out_of_credits"
  | "error";

export interface TaskLiveInputs {
  /** Live socket phase from taskStore — only present for subscribed tasks. */
  livePhase?: TaskPhase;
  liveError?: boolean;
  /** Story-spine snapshot off the board row (historyStore); NULL pre-spine. */
  storyPhase?: string | null;
  /** Legacy persisted lifecycle off the board row. */
  lifecycleState?: string | null;
}

/**
 * The word a status wears — the board's own vocabulary (WorkingNowSection says
 * "Queued"): one state, one word, every surface. The ladder above decides the
 * state and this table says it, so the pill's receipt, the home card's, the
 * crown's and a sheet's source line can never read the same task two ways.
 */
export const STATUS_WORD: Record<TaskLiveStatus, string> = {
  queued: "Queued",
  running: "Running",
  needs: "Needs you",
  done: "Done",
  stopped: "Stopped",
  out_of_credits: "Out of credits",
  error: "Failed",
};

export function taskLiveStatus({
  livePhase,
  liveError,
  storyPhase,
  lifecycleState,
}: TaskLiveInputs): TaskLiveStatus {
  if (liveError) return "error";
  if (
    livePhase === "processing" ||
    livePhase === "waiting" ||
    livePhase === "interrupting"
  ) {
    return "running";
  }
  if (storyPhase) {
    switch (storyPhase) {
      case "done":
        return "done";
      case "stopped":
        return "stopped"; // founder 07-21: an interrupt must never read as "Done"
      case "out_of_credits":
        return "out_of_credits"; // founder 09-03: nor may the ledger's halt read as "Stopped"
      case "failed":
        return "error";
      case "waiting":
        return "needs";
      case "created":
      case "starting":
        return "queued";
      default:
        return "running"; // working | updating | reconnecting | wrapping
    }
  }
  if (lifecycleState) {
    switch (lifecycleState) {
      case "completed":
      case "idle": // a stopped task is not "running in the background"
        return "done";
      case "error":
        return "error";
      case "awaiting":
        return "needs";
      case "scheduled":
      case "queued":
        return "queued";
      default:
        return "running"; // processing
    }
  }
  return "queued";
}
